import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/joy';

import { UnitConversions, roundFloatToInt } from '../../general-utilities/numbers-util';
import { millisecondsToHHMMSS, secondsToHHMMSS } from '../../general-utilities/time-util';
import { TelemetryData } from '../Crew/types';

const Stat: React.FC<{ label: string; value: string; unit?: string }> = ({ label, value, unit }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 500, minWidth: 28, textAlign: 'right' }}>
      {value}
    </Typography>
    {unit && <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>{unit}</Typography>}
  </Box>
);

interface DualTelemetryBarProps {
  telemetry: (TelemetryData | null)[];
  callsigns: string[];
  colors: string[];
}

const DualTelemetryBar: React.FC<DualTelemetryBarProps> = ({ telemetry, callsigns, colors }) => {
  const { t } = useTranslation();
  const ftUnit = t('crew_view.units.feet');
  const mphUnit = t('crew_view.units.mph');

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        bgcolor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '8px',
        padding: '8px 12px',
        backdropFilter: 'blur(4px)'
      }}
    >
      {telemetry.map((td, idx) => {
        const color = colors[idx] || '#fff';
        const name = callsigns[idx] || `Drone ${idx + 1}`;

        const agl = UnitConversions.mToF(td?.position?.relative_altitude_m ?? 0).toFixed(1);
        const asl = UnitConversions.mToF(td?.position?.absolute_altitude_m ?? 0).toFixed(1);
        const airSpeed = UnitConversions.mpsToMph(roundFloatToInt(td?.velocity?.airspeed_m_s)).toFixed(1);
        const groundSpeed = UnitConversions.mpsToMph(td?.velocity?.ground_speed_m_s ?? 0).toFixed(1);
        const time = millisecondsToHHMMSS(td?.flight_info?.duration_since_arming_ms ?? 0);
        const homeDistance = UnitConversions.mToF(td?.home_position?.distance_to_home_m ?? 0).toFixed(1);
        const remainingTime =
          td?.battery?.time_remaining_s && td.battery.time_remaining_s > 0
            ? secondsToHHMMSS(td.battery.time_remaining_s)
            : '--.--';

        return (
          <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 80 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
              <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{name}</Typography>
            </Box>
            <Stat label={t('crew_view.dock.agl')} value={agl} unit={ftUnit} />
            <Stat label={t('crew_view.dock.asl')} value={asl} unit={ftUnit} />
            <Stat label='AS' value={airSpeed} unit={mphUnit} />
            <Stat label='GS' value={groundSpeed} unit={mphUnit} />
            <Stat label='T' value={time} />
            <Stat label='HOME' value={homeDistance} unit={ftUnit} />
            <Stat label='REM' value={remainingTime} />
          </Box>
        );
      })}
    </Box>
  );
};

export default DualTelemetryBar;
